import { useState, useEffect, useCallback, useMemo } from 'react';
import { useReso } from '../contexts/ResoContext';
import { useFieldTranslator } from './FieldTranslator';
import { TaskManager } from './TaskManager';
import { ResourceManagerFactory } from './ResourceManagerFactory';
import { ResourceRegistry } from './ResourceRegistry';
import { ListingResource } from '../system/field';
import {
  ResoResourceBase,
  ResoResourceManager,
  ResourceManagerOptions,
} from './types';
import { BaseResourceManager } from './BaseResourceManager';

/**
 * Options for the useResoResource hook
 */
export interface UseResoResourceOptions extends ResourceManagerOptions {
  enableCache?: boolean;
  cacheTTL?: number;
  autoPage?: number;
}

/**
 * Result type for the useResoResource hook
 */
export interface UseResoResourceResult<T extends ResoResourceBase> {
  // Original resource manager
  resourceManager: ResoResourceManager<T>;

  // Status flags
  isLoading: boolean;
  error: Error | null;

  // Enhanced methods with caching
  find: (predicate: (item: T) => boolean) => Promise<T | null>;
  findByField: (field: string, value: any) => Promise<T | null>;
  findByFields: (fields: Record<string, any>) => Promise<T | null>;
  findListByField: (field: string, value: any) => Promise<T[]>;
  findListByFields: (fields: Record<string, any>) => Promise<T[]>;
  list: (listingKey?: string) => Promise<T[]>;

  // Cache operations
  invalidateCache: () => void;
}

/**
 * Hook for accessing and managing RESO resources with caching
 */
export function useResoResource<T extends ResoResourceBase>(
  resourceType: ListingResource | string,
  options: UseResoResourceOptions = {},
): UseResoResourceResult<T> {
  const { executeQuery } = useReso();
  const fieldTranslator = useFieldTranslator(resourceType);
  const taskManager = TaskManager.getInstance();
  const registry = ResourceRegistry.getInstance();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Default options
  const {
    enableCwa = false,
    useTranslation = true,
    enableCache = true,
    autoPage = undefined,
    cacheTTL,
  } = options;

  // Get or create resource manager
  const resourceManager: BaseResourceManager<T> = useMemo(() => {
    // Check if it already exists in registry
    let manager = registry.getResourceManager<T>(resourceType);

    if (!manager) {
      // Create new manager
      manager = ResourceManagerFactory.createResourceManager<T>(
        resourceType,
        executeQuery,
        taskManager,
        fieldTranslator,
        { enableCwa, useTranslation, autoPage },
      ) as any;

      // Register the manager
      registry.registerResourceManager(resourceType, manager!);
    } else {
      // Update the executeQuery reference on the existing manager
      manager.updateExecuteQuery(executeQuery);
    }

    return manager;
  }, [
    resourceType,
    executeQuery,
    fieldTranslator,
    enableCwa,
    useTranslation,
    options.autoPage,
  ])!;

  // Get resource cache
  const resourceCache = useMemo(() => {
    return registry.getResourceCache<T>(resourceType);
  }, [resourceType]);

  // Enhanced find method with caching
  const find = useCallback(
    async (predicate: (item: T) => boolean): Promise<T | null> => {
      try {
        if (!enableCache) {
          return resourceManager.find(predicate);
        }

        // Use "find" as the cache key with the predicate's toString
        const cacheKey = { type: 'find', predicate: predicate.toString() };

        // Check cache first
        if (resourceCache.has(cacheKey)) {
          return resourceCache.get<T | null>(cacheKey) ?? null;
        }

        // Check for pending query
        if (resourceCache.hasPendingQuery(cacheKey)) {
          return resourceCache.getPendingQuery<T | null>(cacheKey) ?? null;
        }

        // Start new query
        setIsLoading(true);

        const queryPromise = resourceManager
          .find(predicate)
          .then((result) => {
            // Store in cache
            resourceCache.set(cacheKey, result, cacheTTL);
            setIsLoading(false);
            return result;
          })
          .catch((err) => {
            setError(err instanceof Error ? err : new Error(String(err)));
            setIsLoading(false);
            throw err;
          });

        // Store pending query
        resourceCache.setPendingQuery(cacheKey, queryPromise);

        return queryPromise;
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
        return null;
      }
    },
    [resourceManager, resourceCache, enableCache, cacheTTL],
  );

  // Enhanced findByField method with caching
  const findByField = useCallback(
    async (field: string, value: any): Promise<T | null> => {
      try {
        if (!enableCache) {
          return resourceManager.findByField(field, value);
        }

        // Use "findByField" as the cache key
        const cacheKey = { type: 'findByField', field, value };

        // Check cache first
        if (resourceCache.has(cacheKey)) {
          return resourceCache.get<T | null>(cacheKey) ?? null;
        }

        // Check for pending query
        if (resourceCache.hasPendingQuery(cacheKey)) {
          return resourceCache.getPendingQuery<T | null>(cacheKey) ?? null;
        }

        // Start new query
        setIsLoading(true);

        const queryPromise = resourceManager
          .findByField(field, value)
          .then((result) => {
            // Store in cache
            resourceCache.set(cacheKey, result, cacheTTL);
            setIsLoading(false);
            return result;
          })
          .catch((err) => {
            setError(err instanceof Error ? err : new Error(String(err)));
            setIsLoading(false);
            throw err;
          });

        // Store pending query
        resourceCache.setPendingQuery(cacheKey, queryPromise);

        return queryPromise;
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
        return null;
      }
    },
    [resourceManager, resourceCache, enableCache, cacheTTL],
  );

  // Enhanced findByFields method with caching
  const findByFields = useCallback(
    async (fields: Record<string, any>): Promise<T | null> => {
      try {
        if (!enableCache) {
          return resourceManager.findByFields(fields);
        }

        // Use "findByFields" as the cache key
        const cacheKey = { type: 'findByFields', fields };

        // Check cache first
        if (resourceCache.has(cacheKey)) {
          return resourceCache.get<T | null>(cacheKey) ?? null;
        }

        // Check for pending query
        if (resourceCache.hasPendingQuery(cacheKey)) {
          return resourceCache.getPendingQuery<T | null>(cacheKey) ?? null;
        }

        // Start new query
        setIsLoading(true);

        const queryPromise = resourceManager
          .findByFields(fields)
          .then((result) => {
            // Store in cache
            resourceCache.set(cacheKey, result, cacheTTL);
            setIsLoading(false);
            return result;
          })
          .catch((err) => {
            setError(err instanceof Error ? err : new Error(String(err)));
            setIsLoading(false);
            throw err;
          });

        // Store pending query
        resourceCache.setPendingQuery(cacheKey, queryPromise);

        return queryPromise;
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
        return null;
      }
    },
    [resourceManager, resourceCache, enableCache, cacheTTL],
  );

  const findListByField = useCallback(
    async (field: string, value: any): Promise<T[]> => {
      try {
        if (!enableCache) {
          return resourceManager.findListByField(field, value);
        }

        // Use "findListByField" as the cache key
        const cacheKey = { type: 'findListByField', field, value };

        // Check cache first
        if (resourceCache.has(cacheKey)) {
          return resourceCache.get<T[]>(cacheKey) ?? [];
        }

        // Check for pending query
        if (resourceCache.hasPendingQuery(cacheKey)) {
          return resourceCache.getPendingQuery<T[]>(cacheKey) ?? [];
        }

        // Start new query
        setIsLoading(true);

        const queryPromise = resourceManager
          .findListByField(field, value)
          .then((result) => {
            // Store in cache
            resourceCache.set(cacheKey, result, cacheTTL);
            setIsLoading(false);
            return result;
          })
          .catch((err) => {
            setError(err instanceof Error ? err : new Error(String(err)));
            setIsLoading(false);
            throw err;
          });

        // Store pending query
        resourceCache.setPendingQuery(cacheKey, queryPromise);

        return queryPromise;
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
        return [];
      }
    },
    [resourceManager, resourceCache, enableCache, cacheTTL],
  );

  // Enhanced findListByFields method with caching
  const findListByFields = useCallback(
    async (fields: Record<string, any>): Promise<T[]> => {
      try {
        if (!enableCache) {
          return resourceManager.findListByFields(fields);
        }

        // Use "findListByFields" as the cache key
        const cacheKey = { type: 'findListByFields', fields };

        // Check cache first
        if (resourceCache.has(cacheKey)) {
          return resourceCache.get<T[]>(cacheKey) ?? [];
        }

        // Check for pending query
        if (resourceCache.hasPendingQuery(cacheKey)) {
          return resourceCache.getPendingQuery<T[]>(cacheKey) ?? [];
        }

        // Start new query
        setIsLoading(true);

        const queryPromise = resourceManager
          .findListByFields(fields)
          .then((result) => {
            // Store in cache
            resourceCache.set(cacheKey, result, cacheTTL);
            setIsLoading(false);
            return result;
          })
          .catch((err) => {
            setError(err instanceof Error ? err : new Error(String(err)));
            setIsLoading(false);
            throw err;
          });

        // Store pending query
        resourceCache.setPendingQuery(cacheKey, queryPromise);

        return queryPromise;
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
        return [];
      }
    },
    [resourceManager, resourceCache, enableCache, cacheTTL],
  );

  // Enhanced list method with caching
  const list = useCallback(
    async (listingKey?: string): Promise<T[]> => {
      try {
        if (!enableCache) {
          return resourceManager.list(listingKey);
        }

        // Use "list" as the cache key
        const cacheKey = { type: 'list', listingKey };

        // Check cache first
        if (resourceCache.has(cacheKey)) {
          return resourceCache.get<T[]>(cacheKey) ?? [];
        }

        // Check for pending query
        if (resourceCache.hasPendingQuery(cacheKey)) {
          return resourceCache.getPendingQuery<T[]>(cacheKey) ?? [];
        }

        // Start new query
        setIsLoading(true);

        const queryPromise = resourceManager
          .list(listingKey)
          .then((result) => {
            // Store in cache
            resourceCache.set(cacheKey, result, cacheTTL);
            setIsLoading(false);
            return result;
          })
          .catch((err) => {
            setError(err instanceof Error ? err : new Error(String(err)));
            setIsLoading(false);
            throw err;
          });

        // Store pending query
        resourceCache.setPendingQuery(cacheKey, queryPromise);

        return queryPromise;
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
        return [];
      }
    },
    [resourceManager, resourceCache, enableCache, cacheTTL],
  );

  // Cache invalidation
  const invalidateCache = useCallback(() => {
    resourceCache.invalidate();
  }, [resourceCache]);

  // Reset loading and error states when resource type changes
  useEffect(() => {
    setIsLoading(false);
    setError(null);
  }, [resourceType]);

  return {
    resourceManager,
    isLoading,
    error,
    find,
    findByField,
    findByFields,
    findListByField,
    findListByFields,
    list,
    invalidateCache,
  };
}
